import { Rive } from '@rive-app/canvas';

/**
 * Main-thread profile: where the main thread spends its time, printed to the
 * DevTools console once a minute as a share of that minute.
 *
 * Opt-in, and a no-op when off — nothing is patched, and {@link span} is one
 * boolean check around the call it wraps:
 *   - append `?perf` to the URL (one session), or
 *   - `localStorage.setItem('echoback:perf', '1')` (sticky across reloads).
 * Read once at module load.
 *
 * Two independent views, printed side by side (they overlap, so they are not
 * summed):
 *
 * 1. **Spans** — wall time of instrumented parts, at any length:
 *    - `rive:<artboard>#<n>` — one Rive instance's per-frame `draw()` (advance
 *      the state machine + record its draw commands), numbered per artboard so
 *      two Genies are two rows; `(not playing)` marks draws of an instance
 *      whose state machines were paused at the time. Patched on the prototype.
 *    - `rive: paint (all canvases)` — the rest of the Rive runtime's batched
 *      animation-frame callback: the Canvas2D flush that actually paints every
 *      queued canvas.
 *    - `rAF: other` — every other `requestAnimationFrame` callback (the Growth
 *      flip, the page peel, the shell watchdog's frame counter, …).
 *    - plus explicit {@link span} call sites: the View Transition update
 *      callback (a tab switch's whole synchronous effect cascade runs there),
 *      Firestore snapshot callbacks, the shell resize handler.
 *
 * 2. **Long frames** (Long Animation Frames API, Chromium) — every frame that
 *    took ≥ 50 ms, the ones a user feels: total time, blocking time, style &
 *    layout time, and the scripts inside them grouped by source file and
 *    invoker (event handler, promise continuation, timer, …). This is the
 *    catch-all for work no span covers: Firebase SDK, response handling,
 *    Solid effects outside a transition.
 *
 * Percentages are of the window's VISIBLE time: a hidden page is paused (and
 * renders nothing), so counting it would dilute every number.
 */

const WINDOW_MS = 60_000;

const enabled = (() => {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).has('perf')) return true;
    return window.localStorage.getItem('echoback:perf') === '1';
  } catch {
    return false;
  }
})();

/** Whether `?perf` is on this session. The weekly field sample stays off then
 *  (services/perfSample.ts): this profile's own reporting would land in its windows. */
export const mainThreadProfileOn = enabled;

interface Tally {
  ms: number;
  calls: number;
  max: number;
}

let spans = new Map<string, Tally>();
let frameScripts = new Map<string, Tally>();
let longFrames = { count: 0, ms: 0, blockingMs: 0, styleLayoutMs: 0 };
let windowStart = 0;
let hiddenSince: number | null = null;
let hiddenMs = 0;

function add(map: Map<string, Tally>, key: string, ms: number): void {
  const t = map.get(key);
  if (t) {
    t.ms += ms;
    t.calls += 1;
    if (ms > t.max) t.max = ms;
  } else {
    map.set(key, { ms, calls: 1, max: ms });
  }
}

/**
 * Run `fn`, attributing its wall time to `label` when profiling is on. Returns
 * what `fn` returns. Nested spans each record their own full time.
 */
export function span<T>(label: string, fn: () => T): T {
  if (!enabled) return fn();
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    add(spans, label, performance.now() - t0);
  }
}

/** Time spent inside patched Rive `draw()` calls during the current rAF callback. */
let riveDrawMsInCallback = 0;
let riveDrewInCallback = false;

/** Per-instance number within its artboard name, so two Genies are two rows. */
const instanceIds = new WeakMap<object, number>();
const instanceCounts = new Map<string, number>();

function instanceLabel(rive: object, artboard: string): string {
  let n = instanceIds.get(rive);
  if (n === undefined) {
    n = (instanceCounts.get(artboard) ?? 0) + 1;
    instanceCounts.set(artboard, n);
    instanceIds.set(rive, n);
  }
  return `${artboard}#${n}`;
}

function patchRiveDraw(): void {
  // `draw` / `activeArtboard` / `isPlaying` are runtime members the typings
  // keep private or loose. `_boundDraw` is bound at construction, so this must
  // run before any `new Rive()` — it does, from main.tsx.
  type RiveThis = { readonly activeArtboard: string; readonly isPlaying: boolean };
  const proto = Rive.prototype as unknown as {
    draw: (this: RiveThis, time: number, onSecond?: unknown) => void;
  };
  const original = proto.draw;
  proto.draw = function (time, onSecond) {
    // Judged on entry: a draw of an instance that is NOT playing is a one-off
    // (load, resize, `play()`'s first frame) — or a pause that isn't holding.
    const playing = this.isPlaying;
    const t0 = performance.now();
    try {
      original.call(this, time, onSecond);
    } finally {
      const ms = performance.now() - t0;
      riveDrawMsInCallback += ms;
      riveDrewInCallback = true;
      const label = instanceLabel(this, this.activeArtboard || '(loading)');
      add(spans, `rive:${label}${playing ? '' : ' (not playing)'}`, ms);
    }
  };
}

function patchRequestAnimationFrame(): void {
  // The Rive runtime batches every instance into ONE rAF callback and then
  // flushes the Canvas2D work; it looks `requestAnimationFrame` up globally on
  // each call, so wrapping the global covers it.
  const original = window.requestAnimationFrame.bind(window);
  window.requestAnimationFrame = (cb: FrameRequestCallback): number =>
    original((time) => {
      riveDrawMsInCallback = 0;
      riveDrewInCallback = false;
      const t0 = performance.now();
      try {
        cb(time);
      } finally {
        const total = performance.now() - t0;
        if (riveDrewInCallback)
          add(spans, 'rive: paint (all canvases)', total - riveDrawMsInCallback);
        else add(spans, 'rAF: other', total);
      }
    });
}

interface LoafScript {
  readonly duration: number;
  readonly invokerType?: string;
  readonly invoker?: string;
  readonly sourceURL?: string;
  readonly sourceFunctionName?: string;
}
interface LoafEntry extends PerformanceEntry {
  readonly blockingDuration: number;
  readonly renderStart: number;
  readonly styleAndLayoutStart: number;
  readonly scripts: readonly LoafScript[];
}

const fileOf = (url: string | undefined): string => {
  if (!url) return '(no source)';
  try {
    const u = new URL(url);
    const name = u.pathname.split('/').pop() || u.pathname;
    return u.origin === location.origin ? name : `${u.host}/${name}`;
  } catch {
    return url;
  }
};

function observeLongFrames(): boolean {
  if (!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) return false;
  new PerformanceObserver((list) => {
    for (const e of list.getEntries() as LoafEntry[]) {
      // A hidden page's time is excluded from the window, so its frames must be
      // too — counted, they pushed the share past 100%.
      if (hiddenSince !== null && e.startTime >= hiddenSince) continue;
      longFrames.count += 1;
      longFrames.ms += e.duration;
      longFrames.blockingMs += e.blockingDuration;
      if (e.styleAndLayoutStart > 0)
        longFrames.styleLayoutMs += e.startTime + e.duration - e.styleAndLayoutStart;
      for (const s of e.scripts) {
        const fn = s.sourceFunctionName ? ` ${s.sourceFunctionName}()` : '';
        add(frameScripts, `${fileOf(s.sourceURL)}${fn} · ${s.invokerType ?? '?'}`, s.duration);
      }
    }
  }).observe({ type: 'long-animation-frame', buffered: false });
  return true;
}

const r1 = (n: number): number => Math.round(n * 10) / 10;

function rows(map: Map<string, Tally>, visibleMs: number, limit: number) {
  return [...map.entries()]
    .sort((a, b) => b[1].ms - a[1].ms)
    .slice(0, limit)
    .map(([part, t]) => ({
      part,
      ms: Math.round(t.ms),
      '% of window': r1((t.ms / visibleMs) * 100),
      calls: t.calls,
      'avg ms': r1(t.ms / t.calls),
      'max ms': Math.round(t.max),
    }));
}

function report(loafSupported: boolean): void {
  const now = performance.now();
  if (hiddenSince !== null) {
    hiddenMs += now - hiddenSince;
    hiddenSince = now;
  }
  const visibleMs = now - windowStart - hiddenMs;
  if (visibleMs >= 1000) {
    const spanRows = rows(spans, visibleMs, 25);
    const spanTotal = [...spans.values()].reduce((sum, t) => sum + t.ms, 0);
    const top = spanRows
      .slice(0, 3)
      .map((r) => `${r.part} ${r['% of window']}%`)
      .join(' · ');
    const lf = loafSupported
      ? ` · long frames ${r1((longFrames.ms / visibleMs) * 100)}% (${longFrames.count})`
      : '';
    console.groupCollapsed(
      `[perf] main thread, ${r1(visibleMs / 1000)} s visible: spans ${r1((spanTotal / visibleMs) * 100)}% — ${top || 'nothing'}${lf}`,
    );
    console.log('Spans (instrumented parts, any length; nested spans overlap):');
    console.table(spanRows);
    if (loafSupported) {
      console.log(
        `Long frames ≥ 50 ms: ${longFrames.count}, ${Math.round(longFrames.ms)} ms ` +
          `(${r1((longFrames.ms / visibleMs) * 100)}%), blocking ${Math.round(longFrames.blockingMs)} ms, ` +
          `style & layout ${Math.round(longFrames.styleLayoutMs)} ms. Scripts inside them:`,
      );
      console.table(rows(frameScripts, visibleMs, 15));
    } else {
      console.log('Long Animation Frames API not supported in this browser — spans only.');
    }
    console.groupEnd();
  }
  spans = new Map();
  frameScripts = new Map();
  longFrames = { count: 0, ms: 0, blockingMs: 0, styleLayoutMs: 0 };
  windowStart = now;
  hiddenMs = 0;
}

/**
 * Install the profile when it is switched on. Call once from main.tsx, before
 * any `new Rive()` and before render.
 */
export function installMainThreadProfile(): void {
  if (!enabled) return;
  patchRiveDraw();
  patchRequestAnimationFrame();
  const loafSupported = observeLongFrames();
  windowStart = performance.now();
  hiddenSince = document.visibilityState === 'hidden' ? windowStart : null;
  document.addEventListener('visibilitychange', () => {
    const now = performance.now();
    if (document.visibilityState === 'hidden') hiddenSince = now;
    else if (hiddenSince !== null) {
      hiddenMs += now - hiddenSince;
      hiddenSince = null;
    }
  });
  // Diagnostics only, and only when switched on: a plain interval is fine here.
  window.setInterval(() => report(loafSupported), WINDOW_MS);
  console.info(
    `[perf] main-thread profile on — a report every ${WINDOW_MS / 1000} s. Off: remove ?perf / localStorage 'echoback:perf'.`,
  );
}
